import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase, callEdgeFunction } from './supabaseClient';
import { usePanelOwnerUserId } from './PanelAccessContext';
import { useShopsFromDb } from './useShopsFromDb';
import { staffDisplayLabel } from './staffRoleUtils';
import {
  parseChecklistWorkbook,
  buildChecklistInsertRows,
  downloadChecklistTemplate,
  normalizeSheetName,
  DEFAULT_SHEET_NAME,
} from './checklistExcelImport';
import './ChecklistPage.css';

function localYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(s) {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function addDaysYmd(ymd, delta) {
  const dt = parseYmd(ymd);
  if (!dt) return ymd;
  dt.setDate(dt.getDate() + delta);
  return localYmd(dt);
}

function formatEntryTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatEntryUser(userById, userId) {
  if (!userId) return 'Bilinmiyor';
  const info = userById.get(userId);
  if (!info) return 'Bilinmiyor';
  if (info.kind === 'staff') {
    const role = staffDisplayLabel(info);
    return role ? `${info.full_name} · ${role}` : info.full_name;
  }
  return info.name || info.email || 'Yönetici';
}

function entryActionLabel(kind) {
  if (kind === 'ok') return 'Tamamlandı';
  if (kind === 'issue') return 'Sorun bildirildi';
  if (kind === 'notes') return 'Gün notu';
  return 'Kayıt';
}

async function fetchChecklistNotesRow(shopId, workDate) {
  const withAuthor = await supabase
    .from('owner_checklist_notes')
    .select('notes, notes_updated_by, updated_at')
    .eq('shop_id', shopId)
    .eq('work_date', workDate)
    .maybeSingle();
  if (!withAuthor.error) return withAuthor;

  const msg = withAuthor.error.message || '';
  if (!msg.includes('notes_updated_by')) {
    return withAuthor;
  }

  return supabase
    .from('owner_checklist_notes')
    .select('notes, updated_at')
    .eq('shop_id', shopId)
    .eq('work_date', workDate)
    .maybeSingle();
}

function ChecklistEntryLog({ entries, userById, loading }) {
  if (loading) {
    return (
      <section className="checklist-entry-log" aria-labelledby="checklist-entry-log-title">
        <h2 id="checklist-entry-log-title" className="checklist-entry-log-title">Giriş kaydı</h2>
        <p className="checklist-entry-log-empty">Yükleniyor…</p>
      </section>
    );
  }

  if (!entries.length) {
    return (
      <section className="checklist-entry-log" aria-labelledby="checklist-entry-log-title">
        <h2 id="checklist-entry-log-title" className="checklist-entry-log-title">Giriş kaydı</h2>
        <p className="checklist-entry-log-empty">Bu gün için henüz veri girişi yok.</p>
      </section>
    );
  }

  return (
    <section className="checklist-entry-log" aria-labelledby="checklist-entry-log-title">
      <h2 id="checklist-entry-log-title" className="checklist-entry-log-title">Giriş kaydı</h2>
      <p className="checklist-entry-log-sub">Bu gün checklist verisini giren kişiler ve zamanlar.</p>
      <ol className="checklist-entry-log-list">
        {entries.map((entry) => (
          <li key={entry.id} className={`checklist-entry-log-item checklist-entry-log-item--${entry.kind}`}>
            <div className="checklist-entry-log-main">
              <span className={`checklist-entry-log-badge checklist-entry-log-badge--${entry.kind}`}>
                {entryActionLabel(entry.kind)}
              </span>
              {entry.taskText ? (
                <span className="checklist-entry-log-task" title={entry.taskText}>{entry.taskText}</span>
              ) : null}
              {entry.preview ? (
                <span className="checklist-entry-log-preview" title={entry.preview}>{entry.preview}</span>
              ) : null}
            </div>
            <div className="checklist-entry-log-meta">
              <span className="checklist-entry-log-user">{formatEntryUser(userById, entry.userId)}</span>
              <span className="checklist-entry-log-time">{formatEntryTime(entry.at)}</span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function TaskCell({ taskId, text, onSave, disabled }) {
  const [value, setValue] = useState(text);
  const baseline = useRef(text);

  useEffect(() => {
    setValue(text);
    baseline.current = text;
  }, [taskId, text]);

  const commit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValue(baseline.current);
      return;
    }
    if (trimmed !== baseline.current) {
      onSave(taskId, trimmed);
      baseline.current = trimmed;
    }
  };

  return (
    <input
      type="text"
      className="cl-cell-input"
      value={value}
      disabled={disabled}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
          e.currentTarget.blur();
        }
        if (e.key === 'Escape') {
          setValue(baseline.current);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

function ChecklistIssuePanel({ description, photoUrl }) {
  if (!description && !photoUrl) {
    return (
      <div className="cl-issue-panel">
        <p className="cl-issue-panel-label">Sorun bildirildi</p>
        <p className="cl-issue-panel-desc cl-issue-panel-desc--muted">Fotoğraf veya açıklama eklenmemiş.</p>
      </div>
    );
  }

  return (
    <div className="cl-issue-panel">
      <p className="cl-issue-panel-label">Sorun bildirimi</p>
      <div className="cl-issue-panel-body">
        {photoUrl ? (
          <a
            href={photoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="cl-issue-panel-photo-link"
            title="Tam boyutta aç"
          >
            <img src={photoUrl} alt="Sorun fotoğrafı" className="cl-issue-panel-photo" />
          </a>
        ) : null}
        {description ? (
          <p className="cl-issue-panel-desc">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

function ChecklistPage() {
  const { rawRows: shops, loading: shopsLoading, error: shopsError } = useShopsFromDb();
  const [userId, setUserId] = useState(null);
  const ownerUserId = usePanelOwnerUserId(userId);
  const [shopId, setShopId] = useState(null);
  const [workDate, setWorkDate] = useState(() => localYmd(new Date()));
  const [tasks, setTasks] = useState([]);
  const [doneByTaskId, setDoneByTaskId] = useState({});
  const [completionByTaskId, setCompletionByTaskId] = useState({});
  const [draft, setDraft] = useState('');
  const [notes, setNotes] = useState('');
  const [noteMeta, setNoteMeta] = useState({ notes_updated_by: null, updated_at: null });
  const [notesStatus, setNotesStatus] = useState('idle'); // idle | saving | saved
  const [entryUserById, setEntryUserById] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importMode, setImportMode] = useState('replace'); // append | replace
  const [importScope, setImportScope] = useState('all'); // current | all
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [activeSheet, setActiveSheet] = useState(DEFAULT_SHEET_NAME);
  const addInputRef = useRef(null);
  const importInputRef = useRef(null);
  const notesBaselineRef = useRef('');
  const notesSaveTimerRef = useRef(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data?.user?.id ?? null));
  }, []);

  useEffect(() => {
    if (!shopsLoading && shops.length) {
      setShopId((prev) => (prev == null ? shops[0].id : prev));
    }
  }, [shopsLoading, shops]);

  const fetchDay = useCallback(async () => {
    if (ownerUserId == null || shopId == null) {
      setTasks([]);
      setDoneByTaskId({});
      setCompletionByTaskId({});
      setNotes('');
      setNoteMeta({ notes_updated_by: null, updated_at: null });
      notesBaselineRef.current = '';
      setNotesStatus('idle');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    const { data: taskRows, error: tErr } = await supabase
      .from('owner_checklist_tasks')
      .select('id, text, sort_order, sheet_name, sheet_order, created_at')
      .eq('shop_id', shopId)
      .order('sheet_order', { ascending: true })
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (tErr) {
      setError(tErr.message);
      setTasks([]);
      setDoneByTaskId({});
      setCompletionByTaskId({});
      setNotes('');
      setNoteMeta({ notes_updated_by: null, updated_at: null });
      notesBaselineRef.current = '';
      setNotesStatus('idle');
      setLoading(false);
      return;
    }

    const list = Array.isArray(taskRows) ? taskRows : [];
    setTasks(list);

    let map = {};
    if (list.length > 0) {
      const ids = list.map((t) => t.id);
      const { data: compRows, error: cErr } = await supabase
        .from('owner_checklist_completions')
        .select('task_id, done, result, issue_description, issue_photo_url, reported_by, updated_at')
        .eq('work_date', workDate)
        .in('task_id', ids);

      if (cErr) {
        setError(cErr.message);
        setDoneByTaskId({});
        setCompletionByTaskId({});
        setLoading(false);
        return;
      }

      const detailMap = {};
      (Array.isArray(compRows) ? compRows : []).forEach((r) => {
        const result = r.result ?? (r.done ? 'ok' : null);
        detailMap[r.task_id] = {
          result,
          issue_description: r.issue_description ?? '',
          issue_photo_url: r.issue_photo_url ?? '',
          reported_by: r.reported_by ?? null,
          updated_at: r.updated_at ?? null,
        };
        map[r.task_id] = result === 'ok' || (!!r.done && result !== 'issue');
      });
      setCompletionByTaskId(detailMap);
    }
    setDoneByTaskId(map);

    const { data: noteRow, error: nErr } = await fetchChecklistNotesRow(shopId, workDate);

    if (nErr) {
      setError(nErr.message);
      setNotes('');
      setNoteMeta({ notes_updated_by: null, updated_at: null });
      notesBaselineRef.current = '';
    } else {
      const text = noteRow?.notes ?? '';
      setNotes(text);
      setNoteMeta({
        notes_updated_by: noteRow?.notes_updated_by ?? null,
        updated_at: noteRow?.updated_at ?? null,
      });
      notesBaselineRef.current = text;
    }
    setNotesStatus('idle');
    setLoading(false);
  }, [ownerUserId, shopId, workDate]);

  useEffect(() => {
    fetchDay();
  }, [fetchDay]);

  useEffect(() => () => {
    if (notesSaveTimerRef.current) clearTimeout(notesSaveTimerRef.current);
  }, []);

  const saveNotes = useCallback(async (text) => {
    if (!ownerUserId || shopId == null) return;
    const trimmed = text ?? '';
    if (trimmed === notesBaselineRef.current) return;

    setNotesStatus('saving');
    setError('');
    const { error: upErr } = await supabase
      .from('owner_checklist_notes')
      .upsert(
        { shop_id: shopId, work_date: workDate, user_id: ownerUserId, notes: trimmed },
        { onConflict: 'shop_id,work_date' },
      );
    if (upErr) {
      setError(upErr.message);
      setNotesStatus('idle');
      return;
    }
    notesBaselineRef.current = trimmed;
    setNotesStatus('saved');
    if (userId) {
      setNoteMeta((prev) => ({
        notes_updated_by: userId,
        updated_at: new Date().toISOString(),
      }));
    }
  }, [ownerUserId, shopId, workDate, userId]);

  const scheduleNotesSave = useCallback((text) => {
    if (notesSaveTimerRef.current) clearTimeout(notesSaveTimerRef.current);
    notesSaveTimerRef.current = setTimeout(() => {
      void saveNotes(text);
    }, 700);
  }, [saveNotes]);

  const handleNotesChange = (value) => {
    setNotes(value);
    setNotesStatus('idle');
    scheduleNotesSave(value);
  };

  const handleNotesBlur = () => {
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
      notesSaveTimerRef.current = null;
    }
    void saveNotes(notes);
  };

  const entryLogEntries = useMemo(() => {
    const entries = [];
    tasks.forEach((task) => {
      const comp = completionByTaskId[task.id];
      if (!comp?.result) return;
      entries.push({
        id: `task-${task.id}`,
        at: comp.updated_at,
        userId: comp.reported_by,
        kind: comp.result,
        taskText: task.text,
      });
    });
    const trimmedNotes = notes.trim();
    if (trimmedNotes && (noteMeta.notes_updated_by || noteMeta.updated_at)) {
      entries.push({
        id: 'notes',
        at: noteMeta.updated_at,
        userId: noteMeta.notes_updated_by,
        kind: 'notes',
        preview: trimmedNotes.length > 120 ? `${trimmedNotes.slice(0, 117)}…` : trimmedNotes,
      });
    }
    return entries.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return tb - ta;
    });
  }, [tasks, completionByTaskId, notes, noteMeta]);

  const entryLogUserIds = useMemo(() => {
    const ids = new Set();
    entryLogEntries.forEach((entry) => {
      if (entry.userId) ids.add(entry.userId);
    });
    return [...ids];
  }, [entryLogEntries]);

  useEffect(() => {
    if (entryLogUserIds.length === 0) {
      setEntryUserById(new Map());
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const map = new Map();
      const { data: staffRows } = await supabase
        .from('staff_directory')
        .select('auth_user_id, full_name, staff_role, manager_kind')
        .in('auth_user_id', entryLogUserIds);

      (staffRows || []).forEach((row) => {
        if (row.auth_user_id) {
          map.set(row.auth_user_id, { kind: 'staff', ...row });
        }
      });

      const missing = entryLogUserIds.filter((id) => !map.has(id));
      if (missing.length > 0) {
        try {
          const users = await callEdgeFunction('admin-get-users-public-info', {
            method: 'POST',
            body: { p_ids: missing },
          });
          (users || []).forEach((u) => {
            if (u.user_id) map.set(u.user_id, { kind: 'user', ...u });
          });
        } catch {
          /* staff names only */
        }
      }

      if (!cancelled) setEntryUserById(map);
    })();

    return () => {
      cancelled = true;
    };
  }, [entryLogUserIds]);

  const sheetTabs = useMemo(() => {
    const byName = new Map();
    tasks.forEach((t) => {
      const name = normalizeSheetName(t.sheet_name);
      const order = t.sheet_order ?? 0;
      if (!byName.has(name)) {
        byName.set(name, { name, order, total: 0, done: 0 });
      }
      const tab = byName.get(name);
      tab.order = Math.min(tab.order, order);
      tab.total += 1;
      if (completionByTaskId[t.id]?.result) tab.done += 1;
    });
    return [...byName.values()].sort(
      (a, b) => a.order - b.order || a.name.localeCompare(b.name, 'tr'),
    );
  }, [tasks, completionByTaskId]);

  const visibleTasks = useMemo(
    () => tasks
      .filter((t) => normalizeSheetName(t.sheet_name) === (activeSheet || DEFAULT_SHEET_NAME))
      .sort((a, b) => a.sort_order - b.sort_order),
    [tasks, activeSheet],
  );

  const addTask = async () => {
    const text = draft.trim();
    if (!text || !ownerUserId || shopId == null) return;
    setError('');
    const sheetName = activeSheet || DEFAULT_SHEET_NAME;
    const sheetTasks = tasks.filter((t) => normalizeSheetName(t.sheet_name) === sheetName);
    const tabMeta = sheetTabs.find((s) => s.name === sheetName);
    const sheetOrder = tabMeta?.order ?? 0;
    const maxOrder = sheetTasks.length ? Math.max(...sheetTasks.map((i) => i.sort_order)) + 1 : 0;
    const { data, error: insErr } = await supabase
      .from('owner_checklist_tasks')
      .insert({
        user_id: ownerUserId,
        shop_id: shopId,
        text,
        sort_order: maxOrder,
        sheet_name: sheetName,
        sheet_order: sheetOrder,
      })
      .select('id, text, sort_order, sheet_name, sheet_order, created_at')
      .single();
    if (insErr) {
      setError(insErr.message);
      return;
    }
    if (data) {
      setTasks((prev) => [...prev, data]);
      setDraft('');
      addInputRef.current?.focus();
    }
  };

  const updateTaskText = async (taskId, text) => {
    setError('');
    const { data, error: upErr } = await supabase
      .from('owner_checklist_tasks')
      .update({ text })
      .eq('id', taskId)
      .select('id, text, sort_order, sheet_name, sheet_order, created_at')
      .single();
    if (upErr) {
      setError(upErr.message);
      return;
    }
    if (data) {
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, text: data.text } : t)));
    }
  };

  const toggle = async (taskId) => {
    const current = !!doneByTaskId[taskId];
    const next = !current;
    setError('');
    const { data, error: upErr } = await supabase
      .from('owner_checklist_completions')
      .upsert(
        {
          task_id: taskId,
          work_date: workDate,
          done: next,
          result: next ? 'ok' : null,
          issue_description: next ? null : undefined,
          issue_photo_url: next ? null : undefined,
          ...(userId ? { reported_by: userId } : {}),
        },
        { onConflict: 'task_id,work_date' },
      )
      .select('task_id, done, result, issue_description, issue_photo_url, reported_by, updated_at')
      .single();
    if (upErr) {
      setError(upErr.message);
      return;
    }
    if (data) {
      const result = data.result ?? (data.done ? 'ok' : null);
      setDoneByTaskId((prev) => ({ ...prev, [data.task_id]: result === 'ok' }));
      setCompletionByTaskId((prev) => ({
        ...prev,
        [data.task_id]: {
          result,
          issue_description: data.issue_description ?? '',
          issue_photo_url: data.issue_photo_url ?? '',
          reported_by: data.reported_by ?? null,
          updated_at: data.updated_at ?? null,
        },
      }));
    }
  };

  const remove = async (taskId) => {
    setError('');
    const { error: delErr } = await supabase.from('owner_checklist_tasks').delete().eq('id', taskId);
    if (delErr) {
      setError(delErr.message);
      return;
    }
    setTasks((prev) => prev.filter((x) => x.id !== taskId));
    setDoneByTaskId((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  const insertChecklistRows = async (rowsToInsert) => {
    const chunkSize = 200;
    const inserted = [];
    for (let i = 0; i < rowsToInsert.length; i += chunkSize) {
      const chunk = rowsToInsert.slice(i, i + chunkSize);
      const { data, error: insErr } = await supabase
        .from('owner_checklist_tasks')
        .insert(chunk)
        .select('id, text, sort_order, sheet_name, sheet_order, created_at');
      if (insErr) throw insErr;
      inserted.push(...(data || []));
    }
    return inserted;
  };

  const runImport = async () => {
    if (!importPreview || !ownerUserId) return;

    const targetShops = importScope === 'all'
      ? shops.map((s) => s.id)
      : (shopId != null ? [shopId] : []);

    if (!targetShops.length) {
      setError('İçe aktarılacak mağaza seçilmedi.');
      return;
    }

    const { sheets, skippedSheets } = importPreview;
    const totalTasks = sheets.reduce((n, s) => n + s.tasks.length, 0);
    const shopLabels = targetShops
      .map((id) => shops.find((s) => s.id === id)?.shop || `#${id}`)
      .join(', ');

    if (importMode === 'replace') {
      const ok = window.confirm(
        importScope === 'all'
          ? `${targetShops.length} mağazanın mevcut checklist şablonları silinip Excel'deki ${sheets.length} sekme (${totalTasks} görev) yüklenecek.\n\nMağazalar: ${shopLabels}\n\nDevam edilsin mi?`
          : `Seçili mağazanın mevcut görevleri silinip Excel'deki ${sheets.length} sekme (${totalTasks} görev) yüklenecek. Devam edilsin mi?`,
      );
      if (!ok) return;
    }

    setImporting(true);
    setError('');
    setImportMessage('');

    try {
      let insertedTotal = 0;

      for (const targetShopId of targetShops) {
        if (importMode === 'replace') {
          const { error: delErr } = await supabase
            .from('owner_checklist_tasks')
            .delete()
            .eq('shop_id', targetShopId);
          if (delErr) throw delErr;
        }

        const existingTasks = importScope === 'all' || targetShopId !== shopId
          ? []
          : tasks;

        const rowsToInsert = buildChecklistInsertRows({
          sheets,
          shopId: targetShopId,
          userId: ownerUserId,
          existingTasks,
          importMode,
        });

        if (!rowsToInsert.length) continue;
        await insertChecklistRows(rowsToInsert);
        insertedTotal += rowsToInsert.length;
      }

      setImportPreview(null);
      await fetchDay();

      const skippedNote = skippedSheets.length
        ? ` (${skippedSheets.length} sayfa atlandı)`
        : '';
      setImportMessage(
        importScope === 'all'
          ? `${targetShops.length} mağazaya ${sheets.length} sekme, toplam ${insertedTotal} görev ${importMode === 'replace' ? 'yüklendi' : 'eklendi'}.${skippedNote}`
          : `${sheets.length} sekme, ${insertedTotal} görev ${importMode === 'replace' ? 'yüklendi' : 'eklendi'}.${skippedNote}`,
      );
      if (sheets[0]?.name) {
        setActiveSheet(sheets[0].name);
      }
    } catch (e) {
      setError(e?.message || 'Excel içe aktarılamadı.');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    setImportMessage('');

    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseChecklistWorkbook(buffer);
      setImportPreview({
        ...parsed,
        fileName: file.name,
      });
    } catch (e) {
      setImportPreview(null);
      setError(e?.message || 'Excel dosyası okunamadı.');
    } finally {
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!sheetTabs.length) {
      setActiveSheet(DEFAULT_SHEET_NAME);
      return;
    }
    if (!sheetTabs.some((t) => t.name === activeSheet)) {
      setActiveSheet(sheetTabs[0].name);
    }
  }, [sheetTabs, activeSheet]);

  useEffect(() => {
    setActiveSheet(DEFAULT_SHEET_NAME);
  }, [shopId]);

  const doneCount = useMemo(
    () => visibleTasks.filter((t) => doneByTaskId[t.id]).length,
    [visibleTasks, doneByTaskId],
  );

  const totalDoneCount = useMemo(
    () => tasks.filter((t) => doneByTaskId[t.id]).length,
    [tasks, doneByTaskId],
  );

  const issueCount = useMemo(
    () => tasks.filter((t) => completionByTaskId[t.id]?.result === 'issue').length,
    [tasks, completionByTaskId],
  );

  const visibleIssueCount = useMemo(
    () => visibleTasks.filter((t) => completionByTaskId[t.id]?.result === 'issue').length,
    [visibleTasks, completionByTaskId],
  );

  const dateLabel = useMemo(() => {
    const dt = parseYmd(workDate);
    if (!dt) return workDate;
    return dt.toLocaleDateString('tr-TR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, [workDate]);

  const todayYmd = localYmd(new Date());
  const noShops = !shopsLoading && shops.length === 0;
  const shopLabel = shops.find((s) => s.id === shopId)?.shop || '';

  return (
    <div className="admin-page-content checklist-page">
      {shopsError ? <p className="checklist-error">{shopsError}</p> : null}
      {error ? <p className="checklist-error" role="alert">{error}</p> : null}
      {importMessage ? <p className="checklist-import-success">{importMessage}</p> : null}

      {!noShops && (
        <>
          <div className="checklist-toolbar">
            <label className="checklist-field">
              <span>Mağaza</span>
              <select
                className="checklist-select"
                value={shopId ?? ''}
                onChange={(e) => setShopId(e.target.value ? Number(e.target.value) : null)}
                disabled={shopsLoading}
                aria-label="Mağaza"
              >
                {shops.map((s) => (
                  <option key={s.id} value={s.id}>
                    #{s.id} — {s.shop || 'İsimsiz'}
                  </option>
                ))}
              </select>
            </label>

            <div className="checklist-date-block">
              <span className="checklist-field-label">Tarih</span>
              <div className="checklist-date-row">
                <button
                  type="button"
                  className="checklist-date-nav"
                  onClick={() => setWorkDate((d) => addDaysYmd(d, -1))}
                  aria-label="Önceki gün"
                >
                  ‹
                </button>
                <input
                  type="date"
                  className="checklist-date-input"
                  value={workDate}
                  onChange={(e) => setWorkDate(e.target.value || workDate)}
                  aria-label="Tarih"
                />
                <button
                  type="button"
                  className="checklist-date-nav"
                  onClick={() => setWorkDate((d) => addDaysYmd(d, 1))}
                  aria-label="Sonraki gün"
                >
                  ›
                </button>
                <button
                  type="button"
                  className="checklist-today-btn"
                  onClick={() => setWorkDate(todayYmd)}
                  disabled={workDate === todayYmd}
                >
                  Bugün
                </button>
              </div>
            </div>

            {shopId != null && !loading ? (
              <p className="checklist-progress">
                <strong>{doneCount}</strong> / {visibleTasks.length} tamam
                {visibleIssueCount > 0 ? (
                  <span className="checklist-progress-issues"> · <strong>{visibleIssueCount}</strong> sorun</span>
                ) : null}
                {sheetTabs.length > 1 ? ` · ${totalDoneCount}/${tasks.length} toplam` : ''}
                {issueCount > visibleIssueCount ? ` · ${issueCount} sorun (tüm sekmeler)` : ''}
                {' · '}{dateLabel}
                {shopLabel ? ` · ${shopLabel}` : ''}
              </p>
            ) : null}

            <div className="checklist-import-block">
              <span className="checklist-field-label">Excel</span>
              <div className="checklist-import-row">
                <label className="checklist-import-mode">
                  <input
                    type="radio"
                    name="checklist-import-scope"
                    value="all"
                    checked={importScope === 'all'}
                    onChange={() => setImportScope('all')}
                    disabled={importing}
                  />
                  Tüm mağazalar ({shops.length})
                </label>
                <label className="checklist-import-mode">
                  <input
                    type="radio"
                    name="checklist-import-scope"
                    value="current"
                    checked={importScope === 'current'}
                    onChange={() => setImportScope('current')}
                    disabled={importing}
                  />
                  Sadece seçili mağaza
                </label>
                <label className="checklist-import-mode">
                  <input
                    type="radio"
                    name="checklist-import-mode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                    disabled={importing}
                  />
                  Listeyi değiştir
                </label>
                <label className="checklist-import-mode">
                  <input
                    type="radio"
                    name="checklist-import-mode"
                    value="append"
                    checked={importMode === 'append'}
                    onChange={() => setImportMode('append')}
                    disabled={importing}
                  />
                  Mevcut listeye ekle
                </label>
                <button
                  type="button"
                  className="checklist-import-btn"
                  disabled={!ownerUserId || importing || loading}
                  onClick={() => importInputRef.current?.click()}
                >
                  {importing ? 'Aktarılıyor…' : 'Excel seç'}
                </button>
                <button
                  type="button"
                  className="checklist-template-btn"
                  disabled={importing}
                  onClick={downloadChecklistTemplate}
                >
                  Şablon indir
                </button>
                <input
                  ref={importInputRef}
                  type="file"
                  className="checklist-import-input"
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  onChange={handleImportFileChange}
                  aria-hidden
                  tabIndex={-1}
                />
              </div>
            </div>
          </div>

          {importPreview ? (
            <section className="checklist-import-preview" aria-live="polite">
              <div className="checklist-import-preview-head">
                <div>
                  <h2 className="checklist-import-preview-title">İçe aktarma önizlemesi</h2>
                  <p className="checklist-import-preview-file">{importPreview.fileName}</p>
                </div>
                <button
                  type="button"
                  className="checklist-import-preview-close"
                  onClick={() => setImportPreview(null)}
                  disabled={importing}
                  aria-label="Önizlemeyi kapat"
                >
                  ×
                </button>
              </div>

              <div className="checklist-import-preview-meta">
                <span>
                  Hedef: {importScope === 'all' ? `Tüm mağazalar (${shops.length})` : (shopLabel || 'Seçili mağaza')}
                </span>
                <span>
                  Mod: {importMode === 'replace' ? 'Listeyi değiştir' : 'Mevcut listeye ekle'}
                </span>
              </div>

              <div className="shift-table-wrap checklist-import-preview-table-wrap">
                <table className="shift-table checklist-import-preview-table">
                  <thead>
                    <tr>
                      <th>Sekme</th>
                      <th>Görev sayısı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.sheets.map((sheet) => (
                      <tr key={sheet.name}>
                        <td>{sheet.name}</td>
                        <td>{sheet.tasks.length}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>Toplam</strong></td>
                      <td>
                        <strong>
                          {importPreview.sheets.reduce((n, s) => n + s.tasks.length, 0)}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {importPreview.skippedSheets?.length ? (
                <p className="checklist-import-preview-skipped">
                  Atlanan sayfalar: {importPreview.skippedSheets.map((s) => `${s.name} (${s.reason})`).join(' · ')}
                </p>
              ) : null}

              <div className="checklist-import-preview-actions">
                <button
                  type="button"
                  className="shift-submit"
                  disabled={importing || !ownerUserId}
                  onClick={() => void runImport()}
                >
                  {importing ? 'Yükleniyor…' : importScope === 'all' ? 'Tüm mağazalara yükle' : 'Yükle'}
                </button>
                <button
                  type="button"
                  className="shift-delete"
                  disabled={importing}
                  onClick={() => setImportPreview(null)}
                >
                  İptal
                </button>
              </div>
            </section>
          ) : null}

          {shopId != null && (
            <div className="checklist-workbook">
            <div className="checklist-sheet-wrap">
              <table className="checklist-sheet">
                <thead>
                  <tr>
                    <th className="cl-col-num">#</th>
                    <th className="cl-col-task">Görev</th>
                    <th className="cl-col-done">✓</th>
                    <th className="cl-col-actions" aria-label="Sil" />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="checklist-loading">Yükleniyor…</td>
                    </tr>
                  ) : visibleTasks.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="checklist-empty">
                        {tasks.length
                          ? 'Bu sekmede görev yok. Alttan yeni görev ekleyin.'
                          : 'Henüz satır yok. Excel içe aktarın veya alttaki satıra görev yazın.'}
                      </td>
                    </tr>
                  ) : (
                    visibleTasks.map((item, idx) => {
                      const done = !!doneByTaskId[item.id];
                      const comp = completionByTaskId[item.id];
                      const isIssue = comp?.result === 'issue';
                      return (
                        <tr key={item.id} className={done ? 'is-done' : isIssue ? 'is-issue' : ''}>
                          <td className="cl-cell-num">{idx + 1}</td>
                          <td className="cl-cell-task">
                            <TaskCell
                              taskId={item.id}
                              text={item.text}
                              onSave={updateTaskText}
                              disabled={!ownerUserId}
                            />
                            {isIssue ? (
                              <ChecklistIssuePanel
                                description={comp.issue_description}
                                photoUrl={comp.issue_photo_url}
                              />
                            ) : null}
                            {comp?.reported_by || comp?.updated_at ? (
                              <p className="cl-entry-meta">
                                {formatEntryUser(entryUserById, comp.reported_by)}
                                {comp.updated_at ? ` · ${formatEntryTime(comp.updated_at)}` : ''}
                              </p>
                            ) : null}
                          </td>
                          <td className="cl-cell-done">
                            {isIssue ? (
                              <span className="cl-issue-badge" title="Sorun bildirildi">!</span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={done}
                                onChange={() => toggle(item.id)}
                                aria-label={`${item.text} tamamlandı`}
                              />
                            )}
                          </td>
                          <td className="cl-cell-actions">
                            <button
                              type="button"
                              className="cl-row-delete"
                              onClick={() => remove(item.id)}
                              aria-label="Satırı sil"
                              title="Sil"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                <tfoot>
                  <tr className="cl-add-row">
                    <td className="cl-cell-num">+</td>
                    <td className="cl-cell-task">
                      <input
                        ref={addInputRef}
                        type="text"
                        className="cl-cell-input"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTask();
                          }
                        }}
                        placeholder="Yeni görev yazın…"
                        aria-label="Yeni görev"
                        disabled={!ownerUserId || loading}
                      />
                    </td>
                    <td className="cl-cell-done" />
                    <td className="cl-cell-actions">
                      <button
                        type="button"
                        className="cl-add-btn"
                        disabled={!ownerUserId || loading || !draft.trim()}
                        onClick={addTask}
                      >
                        Ekle
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {sheetTabs.length > 0 ? (
              <div className="checklist-sheet-tabs" role="tablist" aria-label="Checklist sekmeleri">
                {sheetTabs.map((tab) => (
                  <button
                    key={tab.name}
                    type="button"
                    role="tab"
                    className={`checklist-sheet-tab${activeSheet === tab.name ? ' is-active' : ''}`}
                    aria-selected={activeSheet === tab.name}
                    onClick={() => setActiveSheet(tab.name)}
                  >
                    <span className="checklist-sheet-tab-name">{tab.name}</span>
                    <span className="checklist-sheet-tab-meta">{tab.done}/{tab.total}</span>
                  </button>
                ))}
              </div>
            ) : null}

            <div className="checklist-notes">
              <div className="checklist-notes-head">
                <span className="checklist-notes-label">Gün notları</span>
                <span className="checklist-notes-status" aria-live="polite">
                  {notesStatus === 'saving' ? 'Kaydediliyor…' : notesStatus === 'saved' ? 'Kaydedildi' : ''}
                </span>
              </div>
              {notes.trim() && (noteMeta.notes_updated_by || noteMeta.updated_at) ? (
                <p className="checklist-notes-by">
                  Son düzenleyen: {formatEntryUser(entryUserById, noteMeta.notes_updated_by)}
                  {noteMeta.updated_at ? ` · ${formatEntryTime(noteMeta.updated_at)}` : ''}
                </p>
              ) : null}
              <textarea
                className="checklist-notes-input"
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                onBlur={handleNotesBlur}
                placeholder="Bu gün için notlar, hatırlatmalar veya açıklamalar…"
                aria-label="Gün notları"
                rows={4}
                disabled={!ownerUserId || loading}
              />
            </div>

            <ChecklistEntryLog
              entries={entryLogEntries}
              userById={entryUserById}
              loading={loading}
            />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChecklistPage;
